import { useCallback, useEffect, useState, useRef } from "react";
import { setIsLoadingStatus, setOpenToast } from "../../../../store/helpers/helperReducers";
import { errorMessage } from "../../../../utils/helpers";
import { toTimespanRangeMs } from "../../../../store/helpers/helpers";
import { useSelector } from "react-redux";
import { IExternalPerformanceArgs } from "../../../../store/api/deviceConfig/types";
import { useStoreDispatch } from "../../../../store/hooks";
import {
  useGetActivityPerformanceMutation,
  useGetAlarmsByDeviceKeyMutation,
  useGetDrawerCountsMutation,
  useGetNodesQuery,
} from "../../../../store/api/devices/deviceApiSlice";
import { selectedDeviceSelector } from "../../../../store/api/devices/selectors";
// import { useLocation } from "react-router-dom";
import { paginationModelSelector, selectedNavListItemSelector } from "../../../../store/helpers/helperSelectors";
import { API } from "../../../../globals/globals";
import { currentSiteGroupsSelector, selectedGeoRegionIdsSelector, startDateSelector } from "../../../../store/api/admin/selectors";
import { deviceRegionFilterIdsSelector } from "../../../../store/filters/Nodes/selectors";
import { GridRowParams } from "@mui/x-data-grid";
import { GridEventListener } from "@mui/x-data-grid";
import {
  setDrawerCounts,
  setOpenDeviceDrawer,
  setSelectedDevice,
  setSelectedDeviceAlarmCount,
  setSelectedDeviceAlarms,
} from "../../../../store/api/devices/reducers";
import { useGetDeviceFullDetailMutation } from "../../../../store/api/deviceConfig/deviceConfigApiSlice";
import ProfilePermissions from "../../../../utils/ProfilePermissions";
import { selectProfilePermissionsSelector } from "../../../../store/api/auth/selectors";
import { searchKeywordSelector } from "../../../../store/search/selector";
import { urlParameterSelector } from "../../../../store/filters/Nodes/selectors";
import { initialState } from "../../../../store/helpers/helperInitialState";
import { resetKeyword } from "../../../../store/search/reducer";
import { setStartDate } from "../../../../store/api/admin/reducers";
import { useRegionDisplay } from "../../../../pages/Administrations/AccessControls/Regions/hooks";
import { devicesSelector } from "../../../../store/filters/Nodes/selectors";
import { setRegionFilterIds, setSelectedDeviceSiteGroup } from "../../../../store/filters/Nodes/reducer";
import { setSelectedNavListItems } from "../../../../store/helpers/helperReducers";
import { setSelectedGeoRegion } from "../../../../store/api/admin/reducers";
import { initialGeoRegion } from "../../../../components/helpers";

export const useActivityPerformanceDisplay = (initialNetworkValue?: string) => {
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const [getActivityPerformance] = useGetActivityPerformanceMutation();
  const dispatch = useStoreDispatch();
  const currentRequestId = useRef<number>(0);
  const [healthFilter, setHealthFilter] = useState("all");
  const [networkValue, setNetworkValue] = useState(initialNetworkValue || "LTE");
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [initialData, setInitialData] = useState([]);
  const selectedDevice: IExternalPerformanceArgs = useSelector(selectedDeviceSelector);
  const startDate = useSelector(startDateSelector);
  const [getDrawerCounts] = useGetDrawerCountsMutation();
  const [getDeviceFullDetail] = useGetDeviceFullDetailMutation();
  const [getAlarmsByDeviceKey] = useGetAlarmsByDeviceKeyMutation();
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowClicked, setRowClicked] = useState(false);

  const selectedGroupKey = useSelector(selectedNavListItemSelector);
  const currentSitegroups = useSelector(currentSiteGroupsSelector);
  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector);
  const navRegionFilterIds: string[] = useSelector(deviceRegionFilterIdsSelector);
  const deviceFilterData = useSelector(devicesSelector);
  const { geoRegions } = useRegionDisplay();
  const search = useSelector(searchKeywordSelector);

  // Use filter state from Redux for Region selection
  const regionFilterIds = deviceFilterData.regionFilterIds || [];

  // Derive region options for the dropdown based on global selection
  const regionOptions = (() => {
    if (!geoRegions || !geoRegions.length) return [];
    if (selectedGeoRegionIds && selectedGeoRegionIds.length) {
      return geoRegions
        .filter((r: any) => selectedGeoRegionIds.includes(r.objectKey))
        .slice()
        .sort((a: any, b: any) => (a?.name || "").localeCompare(b?.name || ""));
    }
    return geoRegions
      .slice()
      .sort((a: any, b: any) => (a?.name || "").localeCompare(b?.name || ""));
  })();

  // Build the list of region names to send based on filter state, nav panel, site-group context,
  // or global selection. A selected site group implicitly scopes the request to its owning region.
  // Priority: regionFilterIds > navRegionFilterIds > selected site-group region > selectedGeoRegionIds > all regions
  const regionNames: string[] = (() => {
    if (!geoRegions || !geoRegions.length) return [];
    
    // Highest priority: user-selected filter from dropdown
    if (regionFilterIds && regionFilterIds.length > 0) {
      return geoRegions
        .filter((r: any) => regionFilterIds.includes(r.objectKey))
        .map((r: any) => r.name);
    }
    
    // Second priority: nav panel region selection
    const hasNavRegionFilter = navRegionFilterIds && navRegionFilterIds.length > 0;
    if (hasNavRegionFilter) {
      return geoRegions
        .filter((r: any) => navRegionFilterIds.includes(r.objectKey))
        .map((r: any) => r.name);
    }

    const selectedSiteGroup = currentSitegroups?.find((group: any) => group.objectKey === selectedGroupKey);
    const selectedSiteGroupRegionName = geoRegions.find(
      (region: any) => region.objectKey === selectedSiteGroup?.parentKey
    )?.name;

    if (selectedSiteGroupRegionName) {
      return [selectedSiteGroupRegionName];
    }
    
    // Fourth priority: global region selection
    if (selectedGeoRegionIds && selectedGeoRegionIds.length) {
      return geoRegions
        .filter((r: any) => selectedGeoRegionIds.includes(r.objectKey))
        .map((r: any) => r.name);
    }
    
    // Fallback: all regions
    return geoRegions.map((r: any) => r.name);
  })();

  // Handler for region filter changes (matches GeneralFilter logic)
  const handleRegionChange = (event: any) => {
    const value = event.target.value;
    const selectedIds = typeof value === 'string' ? value.split(',') : value;
    dispatch(setRegionFilterIds(selectedIds));

    // Synchronize with nav panel and global region (same as GeneralFilter)
    const nextRegion = selectedIds.length === 1
      ? geoRegions.find((region: any) => region.objectKey === selectedIds[0])
      : undefined;

    const nextNavItem = selectedIds.length === 0 ? "" : "";

    dispatch(setSelectedNavListItems(nextNavItem));
    dispatch(setSelectedDeviceSiteGroup(""));
    dispatch(setSelectedGeoRegion(nextRegion || initialGeoRegion));
  };

  useEffect(() => {
    if (search?.length > 0 && initialData?.length > 0) {
      const newArr = initialData?.filter((item: any) => item?.hostName?.toLowerCase()?.includes(search?.toLowerCase()))
      setData([...newArr])
    }
    else setData([...initialData])

  }, [initialData, search])

  const selectedSiteGroupObject = currentSitegroups?.find((group: any) => group.objectKey === selectedGroupKey);
  const selectedSiteGroupName = selectedSiteGroupObject?.name || "";
  const groupKeyToApiFilter: Record<string, string> = {
    devices: "allDevices",
    disconnected: "disconnected",
    pmCollected: "pmCollected",
    maintenancemode: "maintenancemode",
    offline: "offline",
  };
  const isGroupKey = Object.prototype.hasOwnProperty.call(groupKeyToApiFilter, selectedGroupKey);
  const groupApiFilterKey = isGroupKey ? groupKeyToApiFilter[selectedGroupKey] : "";
  const groupValue = selectedGroupKey === "pmCollected" ? "false" : "true";

  const fetchActivityPerformanceData = useCallback(async () => {
    const rawTimeSpan = toTimespanRangeMs(startDate);
    const timeSpan = {
      from: Math.floor(Number(rawTimeSpan.from) / 1000),
      to: Math.floor(Number(rawTimeSpan.to) / 1000),
    };
    const isUngrouped = selectedGroupKey === "ungrouped";

    // Guard: skip API call if regions haven't loaded yet (unless ungrouped, which needs no region filter).
    if (!isUngrouped && (!regionNames || regionNames.length === 0)) {
      setData([]);
      setInitialData([]);
      return;
    }

    const parts: string[] = [
      `to=${timeSpan.to}`,
      `from=${timeSpan.from}`,
      `health=${healthFilter?.toUpperCase()}`,
      ...(!isUngrouped ? regionNames.map((region) => `region=${encodeURIComponent(region)}`) : []),
    ];

    if (isUngrouped) {
      parts.push("ungrouped=true");
    } else if (isGroupKey && groupApiFilterKey) {
      parts.push(`${groupApiFilterKey}=${groupValue}`);
    } else if (selectedSiteGroupName) {
      parts.push(`sitegroup=${encodeURIComponent(selectedSiteGroupName)}`);
    }

    const args = {
      filters: parts.join("&"),
      selectedNetwork: networkValue,
    };

    // Increment request ID to track specific request
    const requestId = ++currentRequestId.current;

    setIsLoading(true);
    try {
      let response: any;
      response = await getActivityPerformance(args);

      // Check if response from current request
      if (requestId !== currentRequestId.current) {
        //response from outdated request, ignore it
        return;
      }

      if (response.error || !response.data) {
        throw response.error.data || "Unknown error";
      }
      if (Array.isArray(response?.data)) {
        setData(response?.data || []);
        setInitialData(response?.data || []);
        setIsLoading(false);
        // dispatch(
        //     setOpenToast({
        //         openToast: true,
        //         toastType: "success",
        //         message: "Performance Data has been fetched successfully",
        //     })
        // );
      }
    } catch (error: any) {
      // Only update loading state if still the current request
      if (requestId === currentRequestId.current) {
        // Clear data to avoid showing stale LTE rows when NR fetch fails (and vice versa)
        setData([]);
        setInitialData([]);
        setIsLoading(false);
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "error",
            message: errorMessage(error),
          })
        );
      }
    }
  }, [dispatch, getActivityPerformance, healthFilter, selectedDevice?.type, startDate, networkValue, selectedGroupKey, regionNames.join()]);

  useEffect(() => {
    fetchActivityPerformanceData();
  }, [healthFilter, startDate, networkValue, selectedGroupKey, regionFilterIds, fetchActivityPerformanceData]);

  // Handling fetch to get device Data in drawer on performance page inside nodes

  const handleRowClicked: GridEventListener<"rowClick"> = async (params: GridRowParams<any>) => {
    console.log("PARAMS", params?.id);
    let objectKey: any = params?.id || "";
    if (params.row.modelName === "migration" || params.row.modelName === "unknown") {
      // This is a migrated device that has not yet connected to SNET
      return;
    }
    dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: true, isLoadingAlarms: true }));
    setRowClicked(true);
    dispatch(setOpenDeviceDrawer(true));
    //setSelectedRow(params.row);
    // dispatch(setSelectedDevice(params.row));
    try {
      let configCount: any;
      let deviceData: any;
      let alarmData: any;
      const args = { filters: `key=objectKey&value=${objectKey}&weightType=FULL`, deviceKey: objectKey };

      //handling multiple requests
      const apiPromises = [];
      apiPromises.push(
        getDrawerCounts({
          deviceKey: objectKey,
          filters: "",
        })
      );
      apiPromises.push(getDeviceFullDetail(args));
      permissions?.hasOpCodeGroup("view_events") &&
        apiPromises.push(
          getAlarmsByDeviceKey({
            deviceKey: objectKey,
            filters: "?sort=retention%2Cdesc&page=0&size=50",
          })
        );

      const responses = await Promise.all(apiPromises);

      responses?.forEach((response, index) => {
        if ("error" in response) {
          const { error = "" } = { ...response };
          throw !!error && error;
        }
      });

      responses?.forEach((response, index) => {
        if ("data" in response && index === 0) {
          const { data = "" } = { ...response };
          configCount = data;
        } else if ("data" in response && index === 1) {
          const { data = "" } = { ...response };
          deviceData = data;
        } else if ("data" in response && index === 2) {
          const { data = "" } = { ...response };
          alarmData = data;
        }
      });

      if (!!configCount && !!deviceData && deviceData?.data[0]) {
        dispatch(setDrawerCounts(configCount));
        dispatch(setSelectedDevice({ ...deviceData.data[0], ...deviceData?.deviceParams }));
      }
      if (permissions?.hasOpCodeGroup("view_events") && alarmData) {
        dispatch(setSelectedDeviceAlarms(alarmData?.data));
        dispatch(setSelectedDeviceAlarmCount(alarmData?.totalItems || 0));
      }
      dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
    } catch (error: any) {
      dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
      dispatch(setOpenDeviceDrawer(false));
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };
  const filters = useSelector(urlParameterSelector);
  const keyword = useSelector(searchKeywordSelector);
  const { nodeDisplay = initialState.pagination } = useSelector(paginationModelSelector);
  const [sortOrder, setSortOrderToNode] = useState("name, desc");

  filters.set("sort", sortOrder);
  filters.set("page", nodeDisplay.page.toString());
  filters.set("size", nodeDisplay.pageSize.toString());
  filters.set("search", keyword);

  const args = { filters: filters.toString() };
  const { refetch } = useGetNodesQuery(args, {
    pollingInterval: API.ENDPOINTS.NODES.pollingInterval || API.POLLING_INTERVAL,
  });

  const handleResetFilter = () => {
    dispatch(resetKeyword());
    dispatch(setStartDate(24 * 60 * 60 * 1000));
    dispatch(setRegionFilterIds([]));
    dispatch(setSelectedNavListItems("devices"));
    dispatch(setSelectedDeviceSiteGroup(""));
    dispatch(setSelectedGeoRegion(initialGeoRegion));
    setHealthFilter("all");
  }; 

  // This will force the data to update each time the user clicks the "Devices" tab
  //  and any time the filter selections are changed
  useEffect(() => {
    refetch();
  }, [filters, refetch, keyword]);

  useEffect(() => {
    setData([]);
    setInitialData([]);
  }, [networkValue]);

  return {
    selectedRow,
    setSelectedRow,
    rowClicked,
    setRowClicked,
    data,
    isLoading,
    healthFilter,
    setHealthFilter,
    selectedDevice,
    setIsLoading,
    networkValue,
    setNetworkValue,
    handleRowClicked,
    handleResetFilter,
    regionFilterIds,
    regionOptions,
    handleRegionChange,
    search,
  };
};
